import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

// ─── Maze levels ───
const mazeLevels = [
  {
    name: "ساعد الأرنب يوصل لبيته! 🏠",
    emoji: "🐰",
    target: "🏠",
    grid: [
      ["🐰", "⬜", "🌳", "⬜", "⬜"],
      ["🌳", "⬜", "🌳", "⬜", "🌳"],
      ["⬜", "⬜", "⬜", "⬜", "⬜"],
      ["⬜", "🌳", "🌳", "⬜", "🌳"],
      ["⬜", "⬜", "⬜", "⬜", "🏠"],
    ],
    start: [0, 0],
    end: [4, 4],
  },
  {
    name: "ساعد القطة تلاقي السمكة! 🐟",
    emoji: "🐱",
    target: "🐟",
    grid: [
      ["⬜", "⬜", "🌳", "⬜", "🐱"],
      ["⬜", "🌳", "⬜", "⬜", "⬜"],
      ["⬜", "⬜", "⬜", "🌳", "⬜"],
      ["🌳", "🌳", "⬜", "⬜", "⬜"],
      ["🐟", "⬜", "⬜", "🌳", "⬜"],
    ],
    start: [0, 4],
    end: [4, 0],
  },
  {
    name: "ساعد الدب يجمع العسل! 🍯",
    emoji: "🐻",
    target: "🍯",
    grid: [
      ["⬜", "🌳", "⬜", "⬜", "⬜"],
      ["⬜", "⬜", "⬜", "🌳", "⬜"],
      ["🌳", "⬜", "🌳", "⬜", "⬜"],
      ["⬜", "⬜", "⬜", "⬜", "🌳"],
      ["🐻", "🌳", "⬜", "⬜", "🍯"],
    ],
    start: [4, 0],
    end: [4, 4],
  },
];

interface Props {
  character: string;
  characterName: string;
  onFinish: (score: number, maxScore: number) => void;
  onCorrect: () => void;
}

export default function MazeGame({ character, characterName, onFinish, onCorrect }: Props) {
  const [level, setLevel] = useState(0);
  const [playerPos, setPlayerPos] = useState<[number, number]>([0, 0]);
  const [moves, setMoves] = useState(0);
  const [completed, setCompleted] = useState(0);
  const [trail, setTrail] = useState<string[]>([]);

  const maze = mazeLevels[level];
  const totalLevels = mazeLevels.length;

  useEffect(() => {
    setPlayerPos(maze.start as [number, number]);
    setMoves(0);
    setTrail([`${maze.start[0]}-${maze.start[1]}`]);
  }, [level]);

  const movePlayer = (dir: "up" | "down" | "left" | "right") => {
    const [row, col] = playerPos;
    let newRow = row, newCol = col;
    if (dir === "up") newRow--;
    if (dir === "down") newRow++;
    if (dir === "left") newCol--;
    if (dir === "right") newCol++;

    if (newRow < 0 || newRow >= 5 || newCol < 0 || newCol >= 5) return;
    if (maze.grid[newRow][newCol] === "🌳") return;

    setPlayerPos([newRow, newCol]);
    setMoves(m => m + 1);
    setTrail(prev => [...prev, `${newRow}-${newCol}`]);

    if (newRow === maze.end[0] && newCol === maze.end[1]) {
      onCorrect();
      const next = completed + 1;
      setCompleted(next);
      if (next >= totalLevels) {
        setTimeout(() => onFinish(totalLevels, totalLevels), 800);
      } else {
        setTimeout(() => setLevel(level + 1), 1000);
      }
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <Badge className="bg-teal-100 text-teal-800 border-teal-300 text-sm">
        المرحلة: {level + 1} / {totalLevels} • الخطوات: {moves}
      </Badge>
      <Card className="bg-white/90 backdrop-blur-sm border-2 border-teal-200 shadow-xl rounded-2xl">
        <CardContent className="p-5 space-y-4 text-center">
          <div className="flex items-center justify-center gap-3">
            <img src={character} alt={characterName} className="w-12 h-12 rounded-full border-2 border-teal-300 bg-teal-50 object-cover" />
            <p className="text-sm font-semibold text-teal-700">{maze.name}</p>
          </div>

          {/* Maze grid */}
          <div className="inline-grid gap-1 mx-auto" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
            {maze.grid.map((row, r) =>
              row.map((cell, c) => {
                const isPlayer = playerPos[0] === r && playerPos[1] === c;
                const isEnd = r === maze.end[0] && c === maze.end[1];
                const isStart = r === maze.start[0] && c === maze.start[1];
                const isTrail = trail.includes(`${r}-${c}`);
                const isTree = cell === "🌳";

                return (
                  <motion.div
                    key={`${r}-${c}`}
                    className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center text-2xl border transition-all ${
                      isPlayer ? "bg-teal-200 border-teal-400 scale-110" :
                      isTree ? "bg-green-100 border-green-300" :
                      isEnd ? "bg-amber-100 border-amber-300" :
                      isTrail ? "bg-teal-50 border-teal-200" :
                      "bg-white border-gray-200"
                    }`}
                    animate={isPlayer ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ duration: 0.3 }}
                  >
                    {isPlayer ? maze.emoji :
                     isEnd && !isPlayer ? maze.target :
                     isTree ? "🌳" :
                     isTrail ? "👣" : ""}
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Direction buttons */}
          <div className="flex flex-col items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => movePlayer("up")}
              className="rounded-xl border-2 border-teal-300 text-xl w-14 h-10">⬆️</Button>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => movePlayer("right")}
                className="rounded-xl border-2 border-teal-300 text-xl w-14 h-10">➡️</Button>
              <Button size="sm" variant="outline" onClick={() => movePlayer("down")}
                className="rounded-xl border-2 border-teal-300 text-xl w-14 h-10">⬇️</Button>
              <Button size="sm" variant="outline" onClick={() => movePlayer("left")}
                className="rounded-xl border-2 border-teal-300 text-xl w-14 h-10">⬅️</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
